import {
  IMAGE_SIZE,
  IMAGE_STYLE,
  ImageSize,
  ImageStyle,
} from '@/constants/tools/ai-image-generator'
import { FC, useMemo } from 'react'
import { Area, AreaSection, AreaSectionTitle } from './styles'
import styled from 'styled-components'
import { useTranslation } from 'next-i18next'
import { poppins } from '@/components/common/fonts'
import classNames from 'classnames'

const IMAGE_STYLE_ITEM_WIDTH = 80
const IMAGE_STYLE_ITEM_HEIGHT = 73

const IMAGE_SIZE_ITEM_WIDTH = 108
const IMAGE_SIZE_ITEM_HEIGHT = 94

type OptionsAreaProps = {
  imageStyle?: ImageStyle
  onChangeImageStyle?: (imageStyle: ImageStyle) => void

  imageSize?: ImageSize
  onChangeImageSize?: (imageSize: ImageSize) => void

  disabled?: boolean

  className?: string
}

export const OptionsArea: FC<OptionsAreaProps> = ({
  imageStyle,
  onChangeImageStyle,

  imageSize,
  onChangeImageSize,

  disabled,

  className,
}) => {
  const { t } = useTranslation('common')

  const styleItems = useMemo(
    () =>
      [
        {
          src: '/images/editor/generate/photometric.png',
          label: t('Photorealistic'),
          value: IMAGE_STYLE.PHOTOGRAPHIC,
        },
        {
          src: '/images/editor/generate/vector.png',
          label: t('Vector Image'),
          value: IMAGE_STYLE.VECTOR_IMAGE,
        },
        {
          src: '/images/editor/generate/illustration.png',
          label: t('Illustration'),
          value: IMAGE_STYLE.ILLUSTRATION,
        },
        {
          src: '/images/editor/generate/3d.png',
          label: t('3D'),
          value: IMAGE_STYLE['3D'],
        },
      ] as const,
    [t],
  )

  const sizeItems = useMemo(
    () =>
      [
        {
          src: '/images/editor/generate/landscape.png',
          label: t('Landscape'),
          value: IMAGE_SIZE.LANDSCAPE,
        },
        {
          src: '/images/editor/generate/portrait.png',
          label: t('Portrait'),
          value: IMAGE_SIZE.PORTRAIT,
        },
        {
          src: '/images/editor/generate/square.png',
          label: t('Square'),
          value: IMAGE_SIZE.SQUARE,
        },
      ] as const,
    [t],
  )

  return (
    <OptionsAreaContainer className={className}>
      <AreaSection
        css={{
          marginBottom: '22px',
        }}
      >
        <AreaSectionTitle>{t('Style')}</AreaSectionTitle>

        <ImageStyles>
          {styleItems.map((item) => (
            <ImageStyleButton
              key={item.value}
              onClick={() => onChangeImageStyle?.(item.value)}
              disabled={disabled}
              className={classNames({
                selected: item.value === imageStyle,
              })}
            >
              <BorderedImage
                $backgroundImage={item.src}
                $width={IMAGE_STYLE_ITEM_WIDTH}
                $height={IMAGE_STYLE_ITEM_HEIGHT}
              />
              <ImageStyleTitle>{item.label}</ImageStyleTitle>
            </ImageStyleButton>
          ))}
        </ImageStyles>
      </AreaSection>

      <AreaSection>
        <AreaSectionTitle>{t('Size')}</AreaSectionTitle>

        <ImageSizes>
          {sizeItems.map((item) => (
            <ImageSizeButton
              key={item.value}
              onClick={() => onChangeImageSize?.(item.value)}
              disabled={disabled}
              className={classNames({
                selected: item.value === imageSize,
              })}
            >
              <BorderedImage
                $backgroundImage={item.src}
                $width={IMAGE_SIZE_ITEM_WIDTH}
                $height={IMAGE_SIZE_ITEM_HEIGHT}
              />
              <ImageSizeTitle>{item.label}</ImageSizeTitle>
            </ImageSizeButton>
          ))}
        </ImageSizes>
      </AreaSection>
    </OptionsAreaContainer>
  )
}

const OptionsAreaContainer = styled(Area)`
  && {
    padding-top: 0;
  }

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    border-bottom: 1px solid #f3f3f3;
  }
`

const ImageStyles = styled.div`
  display: flex;
  gap: 12px;

  margin: auto;
  max-width: 100%;
  overflow: auto;
`

/**
 * - &.selected: 선택된 경우
 */
const ImageStyleButton = styled.button`
  /* reset */
  border: 0;
  padding: 0;
  background-color: #ffffff;

  display: flex;
  flex-direction: column;
  gap: 8px;

  cursor: pointer;

  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.8;

    cursor: not-allowed;
  }
`

/**
 * - .selected &: 선택된 경우
 */
const BorderedImage = styled.div<{
  $backgroundImage: string
  $width: number
  $height: number
}>`
  position: relative;

  background-image: url(${({ $backgroundImage }) => $backgroundImage});

  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;

  border-radius: 4px;

  &::after {
    content: '';

    position: absolute;
    inset: 0;

    border: 2px solid transparent;
    border-radius: 4px;

    transition: border-color 0.2s;

    button:enabled:hover & {
      border-color: #3075f6;
    }

    button:enabled.selected & {
      border-color: #3075f6;
    }

    button:disabled.selected & {
      border-color: #777777;
    }
  }
`

const ImageStyleTitle = styled.span`
  font-family: ${poppins.string};
  font-size: 11px;
  line-height: 16px;
  color: #777777;
  opacity: 0.8;
`

const ImageSizes = styled.div`
  display: flex;
  gap: 12px;

  margin: auto;
  max-width: 100%;
  overflow: auto;
`

const ImageSizeButton = styled.button`
  /* reset */
  border: 0;
  padding: 0;
  background-color: #ffffff;

  display: flex;
  flex-direction: column;
  gap: 8px;

  cursor: pointer;
`

const ImageSizeTitle = styled.span`
  font-family: ${poppins.string};
  font-size: 11px;
  line-height: 16px;
  color: #777777;
  opacity: 0.8;
`
